import React, { useState } from 'react';
import {
  Modal,
  Steps,
  Button,
  Card,
  Row,
  Col,
  Alert,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Badge,
  message,
} from 'antd';
import {
  BankOutlined,
  HomeOutlined,
  ApartmentOutlined,
  MedicineBoxOutlined,
  SafetyCertificateOutlined,
  CustomerServiceOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { API_FETCH_BASE } from '../services/api';
import { clearMerchantPromoCode, getMerchantPromoCode } from '../utils/merchantPromoRef';

const { Text, Title } = Typography;
const { TextArea } = Input;

export type TeamCategoryKey =
  | 'enterprise'
  | 'school'
  | 'research_group'
  | 'hospital'
  | 'government';

const TEAM_TYPE_CARDS: {
  key: TeamCategoryKey;
  title: string;
  desc: string;
  icon: React.ReactNode;
  hot?: boolean;
}[] = [
  {
    key: 'enterprise',
    title: '企业/教培机构',
    desc: '适用于各类企业及研究所、机构',
    icon: <BankOutlined style={{ fontSize: 28, color: '#1677ff' }} />,
  },
  {
    key: 'school',
    title: '学校/科研院所',
    desc: '适用于中高等学校/学院、科研院所',
    icon: <HomeOutlined style={{ fontSize: 28, color: '#1677ff' }} />,
  },
  {
    key: 'research_group',
    title: '高校课题组',
    desc: '仅限高校课题团队，且需要认证',
    icon: <ApartmentOutlined style={{ fontSize: 28, color: '#1677ff' }} />,
    hot: true,
  },
  {
    key: 'hospital',
    title: '医院/医学院',
    desc: '适用于各类医院、医学院所',
    icon: <MedicineBoxOutlined style={{ fontSize: 28, color: '#1677ff' }} />,
  },
  {
    key: 'government',
    title: '政府/事业单位',
    desc: '适用于政府和各类党政机关',
    icon: <SafetyCertificateOutlined style={{ fontSize: 28, color: '#1677ff' }} />,
  },
];

const TEAM_SIZE_OPTIONS = ['1-10人', '11-50人', '51-200人', '200人以上'].map((v) => ({
  value: v,
  label: v,
}));
const POSITION_OPTIONS = [
  '负责人',
  'PI/导师',
  '实验室主任',
  '科研人员',
  '行政/助理',
  '学生',
  '其他',
].map((v) => ({ value: v, label: v }));
const INDUSTRY_OPTIONS = [
  '高等院校',
  '科研院所',
  '医疗卫生',
  '生物医药',
  '新材料/化工',
  '电子信息',
  '政府/事业单位',
  '企业',
  '其他',
].map((v) => ({ value: v, label: v }));
const RESEARCH_OPTIONS = [
  '生命科学',
  '化学/材料',
  '医学/药学',
  '物理',
  '计算机/人工智能',
  '环境',
  '综合',
  '其他',
].map((v) => ({ value: v, label: v }));

export interface CreateTeamModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({ open, onClose }) => {
  const { token, login } = useAuth();
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState<TeamCategoryKey | null>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [createdCode, setCreatedCode] = useState('');
  const [createdName, setCreatedName] = useState('');

  const reset = () => {
    setStep(0);
    setCategory(null);
    form.resetFields();
    setCreatedCode('');
    setCreatedName('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const goNextFromType = () => {
    if (!category) {
      message.warning('请选择团队类型');
      return;
    }
    setStep(1);
  };

  const submitCreate = async () => {
    try {
      const values = await form.validateFields();
      if (!token) {
        message.warning('请先登录');
        return;
      }
      if (!category) {
        message.warning('请选择团队类型');
        setStep(0);
        return;
      }
      setLoading(true);
      const res = await fetch(`${API_FETCH_BASE}/api/auth/create-team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          category,
          teamName: values.teamName?.trim(),
          orgName: values.orgName?.trim(),
          teamSize: values.teamSize,
          ownerPosition: values.ownerPosition,
          industry: values.industry,
          researchField: values.researchField,
          intro: values.intro?.trim() || '',
          contactName: values.contactName?.trim() || '',
          contactPhone: values.contactPhone?.trim() || '',
          contactEmail: values.contactEmail?.trim() || '',
          promoCode: getMerchantPromoCode() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || '创建失败');
      }
      clearMerchantPromoCode();
      const u = data.user as Record<string, unknown>;
      login(
        data.token as string,
        String(u.role),
        String(u.username),
        (u.name as string) || String(u.username),
        (u.avatar as string) || undefined,
        Boolean(u.isFirstLogin),
        Boolean(u.passwordChanged),
        u._id != null ? String(u._id) : undefined,
        u.teamId != null ? String(u.teamId) : undefined,
        data.teamCode as string,
        data.teamName as string
      );
      setCreatedCode(String(data.teamCode || ''));
      setCreatedName(String(data.teamName || ''));
      setStep(2);
      message.success('团队创建成功');
    } catch (e: unknown) {
      const err = e as { errorFields?: unknown };
      if (!err.errorFields) {
        message.error(e instanceof Error ? e.message : '创建失败');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="创建团队"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      width={step === 1 ? 820 : 640}
    >
      <Steps
        size="small"
        current={step}
        style={{ marginBottom: 24 }}
        items={[
          { title: '选择类型' },
          { title: '完善信息' },
          { title: '完成创建' },
        ]}
      />

      {step === 0 && (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="不同团队类型提供的服务不同，每人最多允许创建 1 个团队，有问题联系客服"
          />
          <Row gutter={[12, 12]}>
            {TEAM_TYPE_CARDS.map((c) => {
              const inner = (
                <Card
                  hoverable
                  onClick={() => setCategory(c.key)}
                  style={{
                    height: '100%',
                    borderColor: category === c.key ? '#1677ff' : undefined,
                    boxShadow: category === c.key ? '0 0 0 2px rgba(22,119,255,0.25)' : undefined,
                  }}
                  styles={{ body: { padding: 16 } }}
                >
                  <div style={{ marginBottom: 8 }}>{c.icon}</div>
                  <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                    {c.title}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {c.desc}
                  </Text>
                </Card>
              );
              return (
                <Col xs={24} sm={12} key={c.key}>
                  {c.hot ? (
                    <Badge.Ribbon text="热门" color="red">
                      {inner}
                    </Badge.Ribbon>
                  ) : (
                    inner
                  )}
                </Col>
              );
            })}
          </Row>
          <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: 24 }}>
            <Button type="link" icon={<CustomerServiceOutlined />} onClick={() => message.info('如有疑问请联系客服。')}>
              联系客服
            </Button>
            <Button type="primary" onClick={goNextFromType}>
              下一步
            </Button>
          </Space>
        </>
      )}

      {step === 1 && (
        <>
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="teamName"
                  label="团队名称"
                  rules={[{ required: true, message: '请输入团队名称' }]}
                >
                  <Input placeholder="请输入团队名称" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="orgName"
                  label="单位名称"
                  rules={[{ required: true, message: '请输入单位名称' }]}
                >
                  <Input placeholder="请输入团队所属单位名称" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="teamSize" label="团队规模" rules={[{ required: true, message: '请选择' }]}>
                  <Select placeholder="请选择" options={TEAM_SIZE_OPTIONS} allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="ownerPosition" label="你的职务" rules={[{ required: true, message: '请选择' }]}>
                  <Select placeholder="请选择" options={POSITION_OPTIONS} allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="industry" label="所在行业" rules={[{ required: true, message: '请选择' }]}>
                  <Select placeholder="请选择" options={INDUSTRY_OPTIONS} allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="researchField" label="研究领域" rules={[{ required: true, message: '请选择' }]}>
                  <Select placeholder="请选择" options={RESEARCH_OPTIONS} allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="intro" label="团队简介">
                  <TextArea rows={3} placeholder="便于新成员了解团队" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <div
                  style={{
                    borderLeft: '3px solid #1677ff',
                    paddingLeft: 8,
                    marginBottom: 12,
                    fontWeight: 600,
                  }}
                >
                  联系人信息
                </div>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="contactName" label="姓名">
                  <Input placeholder="姓名" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="contactPhone" label="手机号">
                  <Input placeholder="手机号" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="contactEmail" label="邮箱">
                  <Input placeholder="邮箱" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: 8 }}>
            <Button type="link" icon={<CustomerServiceOutlined />} onClick={() => message.info('如有疑问请联系客服。')}>
              联系客服
            </Button>
            <Space>
              <Button onClick={() => setStep(0)}>上一步</Button>
              <Button type="primary" loading={loading} onClick={submitCreate}>
                确认创建
              </Button>
            </Space>
          </Space>
        </>
      )}

      {step === 2 && (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
          <Title level={4} style={{ marginBottom: 8 }}>
            团队已创建
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            您已成为<strong>本团队管理员</strong>（仅管理本实验室业务与成员，与平台「超级管理员」无关）。请将团队编码分享给成员，便于其登录或加入。
          </Text>
          <Card size="small" style={{ maxWidth: 400, margin: '0 auto 24px', textAlign: 'left' }}>
            <div>
              <Text type="secondary">团队名称</Text>
              <div style={{ fontWeight: 600 }}>{createdName}</div>
            </div>
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">团队编码</Text>
              <Typography.Paragraph copyable style={{ marginBottom: 0, fontSize: 18, fontWeight: 600 }}>
                {createdCode}
              </Typography.Paragraph>
            </div>
          </Card>
          <Button type="primary" onClick={handleClose}>
            完成
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default CreateTeamModal;
